import React, { useMemo } from 'react';
import Select from 'react-select';
import {
    SUBSCRIPTION_STATUS_OPTIONS,
    subscriptionStatusCodes,
    selectStyles,
} from '../../utils';
import DateRangePicker from '../common/DateRangePicker/DateRangePicker';
import { MdSearch, MdRefresh } from "react-icons/md";

const SUB = subscriptionStatusCodes();

/**
 * Same filter row as Subscription Records: text fields + device type + status + Search/Reset.
 * Set `includeNameField` for Test Subscription (filters `subscription.name` on the server).
 */
export default function SubscriptionFiltersBar({
    draftFilters,
    setDraftFilters,
    onSearch,
    onReset,
    deviceTypeOptions = [],
    includeNameField = false,
    instanceIdPrefix = 'subs',
}) {
    const deviceTypeSelectOptions = useMemo(() => {
        const fromApi = deviceTypeOptions.map((opt) => ({ value: opt, label: opt }));
        const v = (draftFilters.device_type || '').trim();
        if (v && !fromApi.some((o) => o.value === v)) {
            return [{ value: v, label: v }, ...fromApi];
        }
        return fromApi;
    }, [deviceTypeOptions, draftFilters.device_type]);

    const deviceTypeSelectValue = useMemo(() => {
        const v = (draftFilters.device_type || '').trim();
        if (!v) return null;
        return { value: v, label: v };
    }, [draftFilters.device_type]);

    const statusSelectOptions = useMemo(
        () =>
            Object.entries(SUBSCRIPTION_STATUS_OPTIONS)
                .filter(([key]) => Number(key) !== SUB.neverSubscribed)
                .map(([key, label]) => ({ value: String(key), label })),
        []
    );

    const statusSelectOptionsWithDraft = useMemo(() => {
        const s = draftFilters.status;
        if (s === '' || s === null || s === undefined) return statusSelectOptions;
        const key = String(s);
        if (statusSelectOptions.some((o) => o.value === key)) return statusSelectOptions;
        const label = SUBSCRIPTION_STATUS_OPTIONS[key] || `Status ${key}`;
        return [{ value: key, label }, ...statusSelectOptions];
    }, [draftFilters.status, statusSelectOptions]);

    const statusSelectValue = useMemo(() => {
        const s = draftFilters.status;
        if (s === '' || s === null || s === undefined) return null;
        const key = String(s);
        const label = SUBSCRIPTION_STATUS_OPTIONS[key] || `Status ${key}`;
        return { value: key, label };
    }, [draftFilters.status]);

    const idDev = `${instanceIdPrefix}-device-type`;
    const idStat = `${instanceIdPrefix}-status`;

    return (
        <div className="list-page-filter-bar">
            <div className="list-page-filter-fields row g-3">
                {includeNameField && (
                    <div className="list-page-filter-field col-12 col-lg-4">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Name"
                            aria-label="Name"
                            value={draftFilters.name || ''}
                            onChange={(e) => setDraftFilters((d) => ({ ...d, name: e.target.value }))}
                        />
                    </div>
                )}
                <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Subscription ID"
                        aria-label="Subscription ID"
                        value={draftFilters.subscription_id}
                        onChange={(e) => setDraftFilters((d) => ({ ...d, subscription_id: e.target.value }))}
                    />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Notify Sphere ID"
                        aria-label="Notify Sphere ID"
                        value={draftFilters.notify_sphere_id}
                        onChange={(e) => setDraftFilters((d) => ({ ...d, notify_sphere_id: e.target.value }))}
                    />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Email"
                        aria-label="Email"
                        value={draftFilters.email}
                        onChange={(e) => setDraftFilters((d) => ({ ...d, email: e.target.value }))}
                    />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Phone"
                        aria-label="Phone"
                        value={draftFilters.phone}
                        onChange={(e) => setDraftFilters((d) => ({ ...d, phone: e.target.value }))}
                    />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Device Name"
                        aria-label="Device Name"
                        value={draftFilters.device_model}
                        onChange={(e) => setDraftFilters((d) => ({ ...d, device_model: e.target.value }))}
                    />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <div style={{ minWidth: 0, width: '100%' }}>
                        <Select
                            inputId={idDev}
                            instanceId={idDev}
                            classNamePrefix="ns-sub-device-type"
                            isSearchable
                            isClearable
                            aria-label="Device type"
                            placeholder="All device types"
                            options={deviceTypeSelectOptions}
                            value={deviceTypeSelectValue}
                            onChange={(opt) => setDraftFilters((d) => ({ ...d, device_type: opt?.value ?? '' }))}
                            styles={selectStyles}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                            noOptionsMessage={() => 'No device types'}
                        />
                    </div>
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <div style={{ minWidth: 0, width: '100%' }}>
                        <Select
                            inputId={idStat}
                            instanceId={idStat}
                            classNamePrefix="ns-sub-status"
                            isSearchable
                            isClearable
                            aria-label="Status"
                            placeholder="All Status"
                            options={statusSelectOptionsWithDraft}
                            value={statusSelectValue}
                            onChange={(opt) => setDraftFilters((d) => ({ ...d, status: opt?.value ?? '' }))}
                            styles={selectStyles}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                            noOptionsMessage={() => 'No statuses'}
                        />
                    </div>
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                    <DateRangePicker
                        dateFrom={draftFilters.dateFrom || ''}
                        dateTo={draftFilters.dateTo || ''}
                        preset={draftFilters.datePreset || ''}
                        onChange={({ dateFrom, dateTo, preset }) =>
                            setDraftFilters((d) => ({
                                ...d,
                                dateFrom: dateFrom || '',
                                dateTo: dateTo || '',
                                datePreset: preset || '',
                            }))
                        }
                        label=""
                        placeholder="Created date"
                        allowClear
                    />
                </div>
                <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-4">
                    <div className="add_app">
                        <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onSearch}>
                            <MdSearch />
                            <span>Search</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onReset}>
                            <MdRefresh />
                            <span>Reset</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
